import {
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Radio,
  DatePicker,
  notification,
  Progress,
  Typography,
} from "antd";
import { useContext, useEffect, useState } from "react";
import {
  PlusCircleTwoTone,
  ExclamationCircleOutlined,
  CloudDownloadOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
} from "@ant-design/icons";
import * as tus from "tus-js-client";
import moment from "moment";

import "./styles.css";

import { Context } from "../../context";
import { Fetch, Post, Delete, Put } from "../../fetch";
import { convertIdToKey, handleExportClick } from "../../utils";
import TextArea from "antd/lib/input/TextArea";
import UploadBanners from "../UploadBanners/index";
import MultiCurrencyInput from "../MultiCurrencyInput/index";

const { Column } = Table;
const { Option } = Select;
const { confirm } = Modal;

const isEmptyEpisodeCount = (value) => value == null || value === "";
const toEpisodeCount = (value) =>
  isEmptyEpisodeCount(value) ? 0 : Number(value);

/** DB rule: free_episode_numbers <= numberOfEpisodesFree */
const validateFreeEpisodesPair = (
  numberOfFree,
  freeEpisodeNumbers,
  field = "numberOfEpisodesFree"
) => {
  const numberOfEpisodesFree = toEpisodeCount(numberOfFree);
  const freeEpisodeNumbersValue = toEpisodeCount(freeEpisodeNumbers);

  if (freeEpisodeNumbersValue > numberOfEpisodesFree) {
    const message =
      field === "free_episode_numbers"
        ? "Free Episode Numbers must be less than or equal to Number Of Episodes Free Users"
        : "Number Of Episodes Free Users must be greater than or equal to Free Episode Numbers";
    return Promise.reject(new Error(message));
  }
  return Promise.resolve();
};

const normalizeFreeEpisodeFields = (fields = {}) => {
  const numberOfEpisodesFree =
    toEpisodeCount(fields.numberOfEpisodesFree ?? fields.freeEpisodes);
  const freeEpisodeNumbers = toEpisodeCount(fields.free_episode_numbers);

  if (freeEpisodeNumbers > numberOfEpisodesFree) {
    throw new Error(
      "free_episode_numbers must be less than or equal to numberOfEpisodesFree"
    );
  }

  return {
    numberOfEpisodesFree,
    free_episode_numbers: freeEpisodeNumbers,
  };
};

// ALL webseries calls here go through /v4 (setu_v4): the staging/prod api
// domain routes UNPREFIXED /webseries to the OLD api, whose model silently
// DROPS newer fields (free_episode_numbers, numberOfEpisodesFree, gif, coin
// fields). Same Mongo either way. Never remove /v4.
const ManageVseries = () => {
  const { token, apiUrl, transcodeUrl } = useContext(Context);

  const [series, setSeries] = useState([]);
  const [partners, setPartners] = useState([]);
  const [genres, setGenres] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [actors, setActors] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [producers, setProducers] = useState([]);
  const [availability, setAvailability] = useState("perpetual");
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [seriesId, setSeriesId] = useState();
  const [counter, setCounter] = useState(0);
  const [trailerUploading, setTrailerUploading] = useState(false);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  const [trailer, setTrailer] = useState();
  const [seriesMaster, setSeriesMaster] = useState([]);
  const [monetizationModel, setMonetizationModel] = useState();

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/v4/webseries?pageSize=100000`, token, (result) => {
      const series = convertIdToKey(result.webseries);
      // const series = result.series;

      series.forEach((su) => {
        if (!su.videoUploadDateTime) {
          su.videoUploadDateTime = "NA";
        } else {
          su.videoUploadDateTime = moment(su.videoUploadDateTime).format(
            "DD/MM/YYYY hh:mm A"
          );
        }
      });

      const unpublished = sessionStorage.getItem("unpublished");
      if (unpublished) {
        const result = series.filter((s) => s.availability === "unpublished");
        setSeries(result);
        setSeriesMaster(result);
      } else {
        setSeries(series);
        setSeriesMaster(series);
      }
    });

    Fetch(`${apiUrl}/genres`, token, (result) => {
      setGenres(result.genres);
    });

    Fetch(`${apiUrl}/languages`, token, (result) => {
      setLanguages(result.languages);
    });

    Fetch(`${apiUrl}/artists`, token, (result) => {
      setActors(result.artists);
    });

    Fetch(`${apiUrl}/mediaHouses`, token, (result) => {
      console.log(result.directors);
      setDirectors(result.directors);
      setProducers(result.producers);
    });
    Fetch(`${apiUrl}/partners`, token, (result) => {
      const partners = convertIdToKey(result.partners);
      setPartners(partners);
    });
  }, [apiUrl, token, counter]);

  const handleSubmit = async () => {
    if (modalType === "Edit") {
      setConfirmLoading(true);
      try {
        let fields = await form.validateFields();
        if (fields.startDate)
          fields["startDate"] = fields.startDate.toISOString(true);
        if (fields.endDate)
          fields["endDate"] = fields.endDate.toISOString(true);

        const { numberOfEpisodesFree, free_episode_numbers } =
          normalizeFreeEpisodeFields(fields);

        const payload = {
          ...fields,
          numberOfEpisodesFree,
          free_episode_numbers,
          publishDate: fields.publishDate
            ? fields.publishDate.format("YYYY-MM-DD")
            : null,
          trailer,
        };
        delete payload.freeEpisodes;

        Put(
          `${apiUrl}/v4/webseries/${seriesId}`,
          token,
          payload,
          (result) => {
            if (result.success) {
              notification.success({
                message: "Success",
                description: "Series updated",
              });
              form.resetFields();
              setModalVisible(false);
              setConfirmLoading(false);
              setCounter(counter + 1);
              setSeriesId(null);
            }
            // else if (result.detail.error && result.detail.error === "ticket") {
            //   notification.error({
            //     message: "Error updating series",
            //     description: `${result.detail.message}`,
            //   });
            //   setConfirmLoading(false);
            // }
            else {
              const playlists = result.detail.playlists;
              const promos = result.detail.promos;

              if (playlists.length > 0 && promos.length > 0) {
                notification.error({
                  message: "Error updating series",
                  description: `Please delete from playlists [${playlists.join(
                    ", "
                  )}] and promos [${promos.join(", ")}] first`,
                });
                setConfirmLoading(false);
              } else if (playlists.length > 0) {
                notification.error({
                  message: "Error updating series",
                  description: `Please delete from playlists [${playlists.join(
                    ", "
                  )}] first`,
                });
                setConfirmLoading(false);
              } else if (promos.length > 0) {
                notification.error({
                  message: "Error updating series",
                  description: `Please delete from promos [${promos.join(
                    ", "
                  )}] first`,
                });
                setConfirmLoading(false);
              }
            }
          },
          () => {
            notification.error({
              message: "Edit Series Error",
              description: "Check console for more details",
            });
          }
        );
      } catch (err) {
        notification.error({
          message: "Edit Series Error",
          description: "Required fields are incorrect/missing",
        });
        setConfirmLoading(false);
      }
      return;
    }

    try {
      setConfirmLoading(true);
      const fields = await form.validateFields();
      console.log("fields", fields);
      const { numberOfEpisodesFree, free_episode_numbers } =
        normalizeFreeEpisodeFields(fields);

      const payload = {
        ...fields,
        numberOfEpisodesFree,
        free_episode_numbers,
        publishDate: fields.publishDate
          ? fields.publishDate.format("YYYY-MM-DD")
          : null,
        trailer,
      };
      delete payload.freeEpisodes;

      Post(
        `${apiUrl}/v4/webseries`,
        token,
        payload,
        (result) => {
          if (result.success) {
            notification.success({
              message: "Success",
              description: "Series created",
            });
          }
          form.resetFields();
          setModalVisible(false);
          setCounter(counter + 1);
        },
        () => {
          notification.error({
            message: "Add Series Error",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Add Series Error",
        description: "Required fields are incorrect/missing",
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const showEditModal = (id) => {
    setModalType("Edit");
    setModalVisible(true);
    Fetch(`${apiUrl}/v4/webseries/${id}?admin=${true}`, token, (result) => {
      const {
        startDate,
        endDate,
        publishDate,
        videoUploadDateTime,
        ...others
      } = result;
      const normalizedOthers = {
        ...others,
        hindiTitle: others.hindiTitle ?? others["hindi Title"],
        numberOfEpisodesFree:
          others.numberOfEpisodesFree ?? others.freeEpisodes ?? 0,
        free_episode_numbers: others.free_episode_numbers ?? 0,
      };
      if (startDate && endDate && publishDate) {
        form.setFieldsValue({
          ...normalizedOthers,
          startDate: moment(startDate),
          endDate: moment(endDate),
          publishDate: moment(publishDate),
        });
      } else if (publishDate) {
        form.setFieldsValue({
          ...normalizedOthers,
          publishDate: moment(publishDate),
        });
      } else if (startDate) {
        form.setFieldsValue({ ...normalizedOthers, startDate: moment(startDate) });
      } else if (videoUploadDateTime) {
        form.setFieldsValue({
          ...normalizedOthers,
          videoUploadDateTime: moment(videoUploadDateTime),
        });
      } else {
        form.setFieldsValue(normalizedOthers);
      }
      setSeriesId(id);
      setMonetizationModel(result.model);
    });
  };

  const confirmDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this series?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        Delete(
          `${apiUrl}/v4/webseries/${id}`,
          token,
          (result) => {
            if (result.success) {
              notification.success({
                message: "Succes",
                description: "Series deleted successfully",
              });
              setCounter(counter + 1);
            } else {
              const playlists = result.detail.playlists;
              const promos = result.detail.promos;

              if (playlists.length > 0 && promos.length > 0) {
                notification.error({
                  message: "Error deleting series",
                  description: `Please delete from playlists [${playlists.join(
                    ", "
                  )}] and promos [${promos.join(", ")}] first`,
                });
              } else if (playlists.length > 0) {
                notification.error({
                  message: "Error deleting series",
                  description: `Please delete from playlists [${playlists.join(
                    ", "
                  )}] first`,
                });
              } else if (promos.length > 0) {
                notification.error({
                  message: "Error deleting series",
                  description: `Please delete from promos [${promos.join(
                    ", "
                  )}] first`,
                });
              }
            }
          },
          (err) => {
            notification.error({
              message: "Error deleting series",
              description: "Please check console for more details",
            });
            // console.log(err)
            // if (err === "406") {
            //   notification.error({
            //     message: "Error deleting series",
            //     description: "This series has active ticket users",
            //   });
            // }
            // else {
            //   notification.error({
            //     message: "Error deleting series",
            //     description: "Please check console for more details",
            //   });
            // }
          }
        );
      },
    });
  };

  const handleChange = (e, type) => {
    const file = e.target.files[0];

    setTrailerUploading(true);

    const upload = new tus.Upload(file, {
      endpoint: `${transcodeUrl}/files`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      metadata: {
        filename: file.name,
        filetype: file.type,
      },
      onError: (error) => {
        console.log("Failed because: " + error);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        var percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
        setTrailerUploadProgress(percentage);
        console.log(bytesUploaded, bytesTotal, percentage + "%");
      },
      onSuccess: () => {
        if (type === "trailer") {
          Post(
            `${transcodeUrl}/submitJob`,
            token,
            {
              id: seriesId,
              loc: upload.url.split("/").slice(-1)[0],
              media_type: "series_trailer",
            },
            (result) => {
              console.log(result);
            },
            () => {
              console.error(`Error transcoding content`);
            }
          );
        }

        console.log("Download %s from %s", upload.file.name, upload.url);
      },
    });

    upload.start();
  };

  const handleSearchChange = (seriesId) => {
    setSeriesId(seriesId);
    Fetch(`${apiUrl}/series/${seriesId}`, token, (result) => {
      const series = convertIdToKey([result]);
      series.forEach((s) => {
        if (typeof s["genre"] === "string") {
          s["genre"] = [s["genre"]];
        }

        if (!s.videoUploadDateTime) {
          s.videoUploadDateTime = "NA";
        } else {
          s.videoUploadDateTime = moment(s.videoUploadDateTime).format(
            "DD/MM/YYYY hh:mm A"
          );
        }
      });
      setSeries(series);
    });
  };

  const extractPartnerName = (record) => {
    for (const partner of partners) {
      if (partner.key === record.partner) {
        return partner.name;
      }
    }
  };

  return (
    <div className="manage-episodes-wrapper">
      <Select
        placeholder="Search Vertical Shorts"
        showSearch
        allowClear
        value={seriesId}
        onChange={handleSearchChange}
        onClear={() => setSeries(seriesMaster)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        filterSort={(optionA, optionB) =>
          optionA.children
            .toLowerCase()
            .localeCompare(optionB.children.toLowerCase())
        }
      >
        {seriesMaster.map((s) => (
          <Option value={s.key}>{s.title}</Option>
        ))}
      </Select>
      <Table dataSource={series}>
        <Column title="Name" dataIndex="title" key="title" align="center" />
        {/* <Column
          title="Language"
          dataIndex="language"
          key="language"
          align="center"
          render={(languages) => (
            <>
              {languages.map((l) => (
                <Tag color="blue" key={l}>
                  {l}
                </Tag>
              ))}
            </>
          )}
        /> */}
        <Column
          title="Genres"
          dataIndex="genre"
          key="genre"
          align="center"
          render={(genre) => (
            <>
              {genre.map((g) => (
                <Tag color="blue" key={g}>
                  {g}
                </Tag>
              ))}
            </>
          )}
        />
        <Column
          title="Partner"
          key={"partner"}
          align="center"
          render={(_, record) =>
            record.partner ? extractPartnerName(record) : "-"
          }
        />
        <Column
          title="Availability"
          dataIndex="availability"
          key="availability"
          align="center"
        />
        <Column title="Model" dataIndex="model" key="model" align="center" />
        <Column
          title="Trailer Exists"
          key="trailer"
          align="center"
          render={(_, record) =>
            record.trailer ? <CheckCircleFilled /> : <CloseCircleOutlined />
          }
        />
        <Column
          title="Video Uploaded Date"
          dataIndex="videoUploadDateTime"
          key="videoUploadDateTime"
          align="center"
        />
        <Column
          title="Actions"
          key="actions"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <a onClick={() => showEditModal(record.key)}>Edit</a>
              <a onClick={() => confirmDelete(record.key)}>Delete</a>
            </Space>
          )}
        />
      </Table>
      <PlusCircleTwoTone
        twoToneColor="#FF0091"
        style={{ fontSize: "48px" }}
        className="add-series-btn"
        onClick={() => {
          setModalVisible(true);
          setModalType("Add");
        }}
      />
      <CloudDownloadOutlined
        className="export-btn"
        onClick={() => handleExportClick(apiUrl, token, "series")}
      />
      <Modal
        title={modalType + "Series"}
        visible={modalVisible}
        onOk={handleSubmit}
        okText="Submit"
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={confirmLoading}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ availability: "perpetual" }}
        >
          {modalType === "Edit" && seriesId ? (
            <Form.Item label="Series ID">
              <Typography.Text code copyable>
                {seriesId}
              </Typography.Text>
            </Form.Item>
          ) : null}
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="hindiTitle"
            label="Hindi Title"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug in Url"
            rules={[
              { required: true },
              {
                pattern: new RegExp(/^[a-zA-Z0-9\-]+$/),
                message: "Please enter alphanumeric or - only",
              },
            ]}
          >
            <Input
              size="middle"
              placeholder="Enter only alphanumeric or -"
              disabled={
                modalType !== "Edit"
                  ? false
                  : availability === "unpublished"
                  ? false
                  : true
              }
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true }]}
          >
            <TextArea />
          </Form.Item>
          <Form.Item
            name="hindiDescription"
            label="Hindi Description"
            rules={[{ required: true }]}
          >
            <TextArea />
          </Form.Item>
          <Form.Item
            name="language"
            label="Language"
            rules={[{ required: true }]}
          >
            <Select mode="multiple" placeholder="Choose a language" showSearch>
              {languages.map((language) => (
                <Option value={language.name}>{language.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="hindiLanguage"
            label="Hindi Language"
            rules={[{ required: true }]}
          >
            <Select mode="multiple" placeholder="Choose a language" showSearch>
              {languages.map((language) => (
                <Option value={language.hindiName}>{language.hindiName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="metaTitle"
            label="metaTitle"
            rules={[{ required: true }]}
          >
            <TextArea />
          </Form.Item>
          <Form.Item
            name="metaDescription"
            label="metaDescription"
            rules={[{ required: true }]}
          >
            <TextArea />
          </Form.Item>
          <Form.Item
            name="metaKeywords"
            label="metaKeywords"
            rules={[{ required: true }]}
          >
            <Select
              mode="tags"
              style={{
                width: "100%",
              }}
              tokenSeparators={["Enter"]}
            ></Select>
          </Form.Item>
          <Form.Item
            name="genre"
            label="Genre / Category"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose a genre" mode="multiple" showSearch>
              {genres.map((genre) => (
                <Option value={genre.name}>{genre.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="hindiGenre"
            label="Hindi Genre / Category"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose a genre" mode="multiple" showSearch>
              {genres.map((genre) => (
                <Option value={genre.hindiName}>{genre.hindiName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="maturity"
            label="Maturity"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose maturity">
              <Option value="U">U (Universal)</Option>
              <Option value="U/A 7+">U/A 7+ (For Ages 7 and Above)</Option>
              <Option value="U/A 13+">U/A 13+ (For Ages 13 and Above)</Option>
              <Option value="U/A 16+">U/A 16+ (For Ages 16 and Above)</Option>
              <Option value="A">A (Adults - Ages 18 and Above)</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="hindiMaturity"
            label="Hindi Maturity"
            rules={[{ required: true }]}
          >
            <Select placeholder="Choose maturity">
              <Option value="यू">यू (सर्वजनहिताय)</Option>
              <Option value="यू/ए 7+">यू/ए 7+ (7 साल से ऊपर खातिर)</Option>
              <Option value="यू/ए 13+">यू/ए 13+ (13 साल से ऊपर खातिर)</Option>
              <Option value="यू/ए 16+">यू/ए 16+ (16 साल से ऊपर खातिर)</Option>
              <Option value="ए">ए (सिर्फ बालिग - 18 साल से ऊपर)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="actors" label="Actors" rules={[{ required: true }]}>
            <Select showSearch mode="multiple" className="form-card-selector">
              {actors.map((actor) => (
                <Option key={actor.key} value={actor.name}>
                  {actor.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="hindiActors"
            label="Hindi Actors"
            rules={[{ required: true }]}
          >
            <Select showSearch mode="multiple" className="form-card-selector">
              {actors.map((actor) => (
                <Option key={actor.hindiName} value={actor.hindiName}>
                  {actor.hindiName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="directors"
            label="Directors"
            rules={[{ required: true }]}
          >
            <Select showSearch mode="multiple" className="form-card-selector">
              {directors.map((director) => (
                <Option key={director.key} value={director.name}>
                  {director.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="hindiDirectors"
            label="Hindi Directors"
            rules={[{ required: true }]}
          >
            <Select showSearch mode="multiple" className="form-card-selector">
              {directors.map((director, i) => (
                <Option
                  key={director.hindiName || i}
                  value={director.hindiName}
                >
                  {director.hindiName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="producers"
            label="Producers"
            rules={[{ required: true }]}
          >
            <Select showSearch mode="multiple">
              {producers.map((producer) => (
                <Option key={producer.key} value={producer.name}>
                  {producer.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="hindiProducers"
            label="Hindi Producers"
            rules={[{ required: true }]}
          >
            <Select showSearch mode="multiple">
              {producers?.map((producer, i) => (
                <Option
                  key={producer.hindiName || i}
                  value={producer.hindiName}
                >
                  {producer.hindiName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="year" label="Year" rules={[{ required: true }]}>
            <InputNumber placeholder="Enter" min={0} />
          </Form.Item>
          <Form.Item
            name="availability"
            label="Availability"
            rules={[{ required: true }]}
          >
            <Radio.Group onChange={(e) => setAvailability(e.target.value)}>
              <Radio value="perpetual">Perpetual</Radio>
              <Radio value="restricted">Restricted</Radio>
              <Radio value="unpublished">Unpublished</Radio>
            </Radio.Group>
          </Form.Item>
          {availability === "restricted" && (
            <>
              <Form.Item
                name="startDate"
                label="Start Date"
                className="form-item-selector"
                rules={[{ required: true }]}
              >
                <DatePicker format="YYYY-MM-DD HH:mm:ss" showTime />
              </Form.Item>
              <Form.Item
                name="endDate"
                label="End Date"
                className="form-item-selector"
                format="YYYY-MM-DD HH:mm:ss"
              >
                <DatePicker format="YYYY-MM-DD HH:mm:ss" showTime />
              </Form.Item>
            </>
          )}
          <Form.Item label="Model" name="model" rules={[{ required: true }]}>
            <Radio.Group onChange={(e) => setMonetizationModel(e.target.value)}>
              <Radio value="ticket">Ticket</Radio>
              <Radio value="subscription">Subscription</Radio>
              <Radio value="free">Free</Radio>
            </Radio.Group>
          </Form.Item>
          {monetizationModel === "ticket" && (
            <>
              <Form.Item
                name="streamPeriod"
                label="Streaming Period"
                className="form-item-selector"
                rules={[{ required: true }]}
              >
                <InputNumber
                  min={1}
                  max={30}
                  placeholder="Enter number of days (1-30)"
                />
              </Form.Item>
              <Form.Item
                name="subscriberPrice"
                label="Price for Subscribers"
                className="form-item-selector"
                rules={[{ required: true }]}
              >
                <MultiCurrencyInput
                  form={form}
                  fieldName={"subscriberPrice"}
                ></MultiCurrencyInput>
              </Form.Item>
              <Form.Item
                name="nonSubscriberPrice"
                label="Price for Non Subscribers"
                className="form-item-selector"
                rules={[{ required: true }]}
              >
                <MultiCurrencyInput
                  form={form}
                  fieldName={"nonSubscriberPrice"}
                ></MultiCurrencyInput>
              </Form.Item>
            </>
          )}
          <Form.Item
            name="cardImage"
            label="Card Image"
            rules={[{ required: true }]}
          >
            <UploadBanners
              name={"cardImageSeries"}
              type="series"
              updateForm={form}
            />
          </Form.Item>
          <Form.Item
            name="trailerImage"
            label="Trailer Image"
            rules={[{ required: true }]}
          >
            <UploadBanners
              name={"trailerImage"}
              type="series"
              updateForm={form}
            />
          </Form.Item>
          <Form.Item
            name="longVerticalImage"
            label="Long Vertical Image"
            rules={[{ required: true }]}
          >
            <UploadBanners
              name={"longVerticalImage"}
              type="series"
              updateForm={form}
            />
          </Form.Item>
          <Form.Item
            name="gif"
            label="GIF URL"
            extra="Direct URL of the animated preview for the series card (optional)"
            rules={[{ type: "url", message: "Enter a valid URL" }]}
          >
            <Input placeholder="https://content.minipix.co/gifs/series-preview.gif" />
          </Form.Item>
          <Form.Item
            name="numberOfEpisodes"
            label="Number Of Episodes Paid Users"
            rules={[{ required: true }]}
          >
            <InputNumber placeholder="Enter number of episodes for paid users" min={0} />
          </Form.Item>
          <Form.Item
            name="numberOfEpisodesFree"
            label="Number Of Episodes Free Users"
            dependencies={["free_episode_numbers"]}
            validateTrigger={["onChange", "onBlur"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return validateFreeEpisodesPair(
                    value,
                    getFieldValue("free_episode_numbers"),
                    "numberOfEpisodesFree"
                  );
                },
              }),
            ]}
          >
            <InputNumber
              placeholder="Enter number of episodes for free users"
              min={0}
            />
          </Form.Item>
          <Form.Item
            name="free_episode_numbers"
            label="Free Episode Numbers"
            tooltip="DB field: free_episode_numbers"
            dependencies={["numberOfEpisodesFree"]}
            validateTrigger={["onChange", "onBlur"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  return validateFreeEpisodesPair(
                    getFieldValue("numberOfEpisodesFree"),
                    value,
                    "free_episode_numbers"
                  );
                },
              }),
            ]}
          >
            <InputNumber placeholder="Enter free episode numbers value" min={0} />
          </Form.Item>
          <Form.Item
            name="publishDate"
            label="Publish Date"
            className="form-item-selector"
            format="YYYY-MM-DD"
          >
            <DatePicker format="YYYY-MM-DD" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageVseries;
